import LogRocket from "logrocket";
import Rollbar from "rollbar";

let rollbar;

const MESSAGES_TO_IGNORE = [
  // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/22
  "Object Not Found Matching Id:",
  // Browser-emitted benign warning — fires when a ResizeObserver callback causes layout changes that
  // can't be delivered in the same animation frame. Not actionable and produces no useful stack trace.
  "ResizeObserver loop completed with undelivered notifications.",
];

// Safari reports extension script URLs as `webkit-masked-url://hidden/` rather than the real scheme
const EXTENSION_FRAME_REGEXP =
  /^((chrome|moz|safari(-web)?|ms-browser)-extension|webkit-masked-url):\/\//;

// Errors thrown entirely from browser extension code (e.g. extensions that monkey-patch fetch/XHR)
// aren't actionable and can amplify themselves when Rollbar's own report XHR goes through the patch.
function isFromBrowserExtension(payload) {
  const frames = payload.body.trace?.frames || [];

  return (
    frames.length > 0 &&
    frames.every(({ filename }) => EXTENSION_FRAME_REGEXP.test(filename || ""))
  );
}

export default ({ app }) => {
  if (!import.meta.env.VITE_ROLLBAR_TOKEN) return;

  rollbar = new Rollbar({
    accessToken: import.meta.env.VITE_ROLLBAR_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    checkIgnore(isUncaught, args, payload) {
      if (isFromBrowserExtension(payload)) return true;

      return MESSAGES_TO_IGNORE.some(
        (message) =>
          (payload.body.trace?.exception?.message || "").includes(message) ||
          (payload.body.trace?.exception?.description || "").includes(message)
      );
    },
    enabled: true,
    payload: {
      client: {
        javascript: {
          code_version: "__commit_hash__",
          guess_uncaught_frames: true,
          source_map_enabled: true,
        },
      },
      environment: import.meta.env.VITE_ENV,
    },
    source_map_enabled: true,
    transform(payload) {
      Object.assign(payload, { sessionURL: LogRocket.sessionURL });

      // replace trace domain with GCP hosted copy
      const { trace } = payload.body;

      const locationRegexp =
        /^(https?|capacitor):\/\/([a-zA-Z0-9._-]+\.)?(streem.com.au|cision.one|localhost)(.*)/;

      if (trace && trace.frames) {
        for (let index = 0; index < trace.frames.length; index += 1) {
          const { filename } = trace.frames[index];

          const match = filename?.match(locationRegexp);

          if (match) {
            trace.frames[index].filename =
              `__gcp_bucket_base_href__${match.pop()}`;
          }
        }
      }

      return payload;
    },
  });

  // eslint-disable-next-line no-param-reassign
  app.config.globalProperties.$rollbar = rollbar;
};

function identify(data) {
  if (!import.meta.env.VITE_ROLLBAR_TOKEN) return;

  rollbar.configure({
    payload: {
      person: data,
    },
  });
}

function rollbarError(error) {
  if (rollbar) rollbar.error(error);
}

function rollbarWarning(warning) {
  if (rollbar) rollbar.warning(warning);
}

export { identify, rollbarError, rollbarWarning };
